import LinkIcon from '@mui/icons-material/Link';
import * as React from 'react';
import { useState, useEffect } from 'react';
import { apiGet } from '../api/apiClient';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import StarIcon from '@mui/icons-material/Star';
import EventNoteIcon from '@mui/icons-material/EventNote';
import PolicyIcon from '@mui/icons-material/Policy';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { FormattedText } from '../utils/textFormatter';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import Paper from '@mui/material/Paper';


function getUpdateIcon(icon) {
  switch (icon) {
    case 'star': return <StarIcon sx={{ color: '#FFD700' }} />;
    case 'settings': return <PolicyIcon sx={{ color: '#616161' }} />;
    case 'gift': return <CardGiftcardIcon sx={{ color: '#1976d2' }} />;
    case 'policy': return <PolicyIcon sx={{ color: '#43a047' }} />;
    case 'event': return <EventNoteIcon sx={{ color: '#ff9800' }} />;
    case 'link': return <LinkIcon sx={{ color: '#0d004a' }} />;
    default: return <InfoOutlinedIcon sx={{ color: '#1976d2' }} />;
  }
}

export default function NewRightPanel() {
  const [openDialog, setOpenDialog] = useState(null);
  const [links, setLinks] = useState([]);
  const [linksLoading, setLinksLoading] = useState(false);
  const [linksError, setLinksError] = useState('');
  const [updates, setUpdates] = useState([]);
  const [updatesLoading, setUpdatesLoading] = useState(false);
  const [updatesError, setUpdatesError] = useState('');

  useEffect(() => {
    const fetchUpdates = async () => {
      setUpdatesLoading(true);
      setUpdatesError('');
      try {
        const data = await apiGet('/admin_updates');
        // Map backend fields to notification UI
        setUpdates(Array.isArray(data) ? data.map(item => ({
          id: item.id,
          icon: getUpdateIcon(item.icon_name),
          title: item.title,
          details: item.message,
          subtitle: item.added_by ? `By ${item.added_by}` : '',
          full: item.message,
        })) : []);
      } catch (e) {
        setUpdatesError(e.message || 'Unable to load updates');
      } finally {
        setUpdatesLoading(false);
      }
    };
    fetchUpdates();
    const fetchLinks = async () => {
      setLinksLoading(true);
      setLinksError('');
      try {
        const data = await apiGet('/important_links');
        setLinks(Array.isArray(data) ? data : []);
      } catch (e) {
        setLinksError(e.message || 'Unable to load links');
      } finally {
        setLinksLoading(false);
      }
    };
    fetchLinks();
  }, []);

  const handleOpenDialog = idx => setOpenDialog(idx);
  const handleCloseDialog = () => setOpenDialog(null);
  return (
    <Box
        sx={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: { xs: '33%' },
          height: '100vh',
          bgcolor: '#0D004A', // dark blue background
          boxShadow: '0 4px 24px 0 rgba(13,0,74,0.10)',
          zIndex: 1200,
          pt: { xs: 7, md: 8 },
          px: 2,
          overflowY: 'auto',
          borderLeft: '1px solid #e3e6ee',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {/* Latest News Section */}

          <Typography variant="h8" sx={{ fontWeight: 800, color: 'white', mt:2, mb: 1, textAlign: 'left' }}>
            News Section
          </Typography>

        {/* News Items List */}
        <Box sx={{ 
          flex: 1, 
          overflowY: 'auto',
          pr: 1, // Add padding to create space between content and scrollbar
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#ccc',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#999',
          }
        }}>
          {updatesLoading && (
            <Paper sx={{ p: 2, mb: 1, bgcolor: '#f8f9fa' }}>
              <Typography variant="body2" color="text.secondary">Loading news...</Typography>
            </Paper>
          )}
          {updatesError && (
            <Paper sx={{ p: 2, mb: 1, bgcolor: '#ffebee' }}>
              <Typography variant="body2" color="error">{updatesError}</Typography>
            </Paper>
          )}
          {!updatesLoading && !updatesError && updates.length === 0 && (
            <Paper sx={{ p: 2, mb: 1, bgcolor: '#f8f9fa' }}>
              <Typography variant="body2" color="text.secondary">No news available</Typography>
            </Paper>
          )}
          {updates.map((notif, idx) => (
            <Paper
              key={notif.id}
              elevation={0}
              sx={{
                bgcolor: '#f8f9fa',
                borderRadius: 2,
                p: 2,
                mb: 1,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                border: '1px solid #e9ecef',
                '&:hover': {
                  bgcolor: '#e9ecef',
                  borderColor: '#dee2e6',
                  '& .news-title': {
                    textDecoration: 'underline'
                  },
                  '& .news-arrow': {
                    color: '#ff6b35'
                  }
                }
              }}
              onClick={() => handleOpenDialog(idx)}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <Box sx={{ flex: 1, pr: 1 }}>
                  {/* Icon and Title Row */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    {/* Icon in Circle with Red Outline */}
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mr: 1,
                        flexShrink: 0
                      }}
                    >
                      {notif.icon}
                    </Box>
                    
                    {/* News Title */}
                    <Typography 
                      variant="subtitle2" 
                      className="news-title"
                      sx={{ 
                        fontWeight: 600, 
                        color: '#000', 
                        fontSize: '0.875rem',
                        textAlign: 'left'
                      }}
                    >
                      {notif.title}
                    </Typography>
                  </Box>
                  
                  {/* News Description */}
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: '#666', 
                      fontSize: '0.8rem',
                      lineHeight: 1.4,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textAlignLast: 'left'
                    }}
                  >
                    {notif.details}
                  </Typography>
                </Box>
                
                {/* Arrow Icon */}
                <ArrowForwardIosIcon 
                  className="news-arrow"
                  sx={{ 
                    color: '#000', 
                    fontSize: 16,
                    transition: 'color 0.2s ease',
                    flexShrink: 0
                  }} 
                />
              </Box>
            </Paper>
          ))}
        </Box>

        {/* News Details Dialog */}
        <Dialog 
          open={openDialog !== null} 
          onClose={handleCloseDialog} 
          maxWidth="sm" 
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 2,
              boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
            }
          }}
        >
          <DialogTitle sx={{ pb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#0D004A' }}>
              News Details
            </Typography>
          </DialogTitle>
          <DialogContent>
            {openDialog !== null && updates[openDialog] && (
              <>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#000' }}>
                  {updates[openDialog].title}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6, color: '#333' }}>
                  <FormattedText text={updates[openDialog].full} />
                </Typography>
                {updates[openDialog].subtitle && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    {updates[openDialog].subtitle}
                  </Typography>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button 
              onClick={handleCloseDialog} 
              variant="contained"
              sx={{
                bgcolor: '#0D004A',
                '&:hover': { bgcolor: '#1a0a6e' },
                borderRadius: 2
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Important Links Section */}
        <Accordion
          sx={{
            mb: 1,
            bgcolor: '#0D004A',
            color: '#fff',
            boxShadow: '0 2px 8px 0 rgba(13,0,74,0.04)',
            border: '1px solid #e3e6ee',
            width: '100%',
            '& .MuiAccordionSummary-root, & .MuiAccordionDetails-root': {
              color: '#fff',
            },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#fff' }} />}>
            <Typography sx={{ fontWeight: 600, color: '#fff', width: '100%' }}>Important Links</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 0 }}>
            <List sx={{ width: '100%' }}>
              {linksLoading && (
                <ListItem>
                  <ListItemText primary="Loading..." />
                </ListItem>
              )}
              {linksError && (
                <ListItem>
                  <ListItemText primary={linksError} sx={{ color: 'error.main' }} />
                </ListItem>
              )}
              {!linksLoading && !linksError && links.length === 0 && (
                <ListItem>
                  <ListItemText primary="No links available" />
                </ListItem>
              )}
              {links.map(link => (
                <ListItem button component="a" href={link.url} target="_blank" key={link.id} sx={{ color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' } }}>
                  <LinkIcon sx={{ mr: 1, fontSize: 20, verticalAlign: 'middle' }} />
                  <ListItemText primary={link.title} />
                </ListItem>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      </Box>
  );
}
